// src/components/MsoInputSummary.tsx
//
// ★★ ANNEX A 22 - the input of a stored calculation, read as a bill rather than as JSON.
//
// The enquiry used to print the stored input as raw JSON with amounts in whole cents ("amountCents":
// 13520), which an officer has to translate before they can check it against the paper bill. This reads
// the same record and labels it: fields by their names on the calculator, amounts in dollars, the
// entitlement window in dates.
//
// Two things it deliberately does not do:
//   * It does not HIDE what it cannot read. A key it does not recognise, or an input that is not the
//     shape the engine takes, is shown under "Other recorded fields" with its JSON value, so a stored
//     calculation is never shown as tidier than it was.
//   * It does not replace the record. The exact stored JSON stays one disclosure away, because that is
//     the evidence and a re-formatted copy is a second account of it.
import { formatCents, formatIsoDate } from '../lib/format'
import { humaniseToken, MSO_COMPONENT_LABELS, MSO_FIELD_LABELS } from '../lib/mso-vocabulary'
import { isRedactedField, redactedText } from '../lib/redacted'

const KNOWN = new Set([
  'scheme',
  'beneficiaryRef',
  'treatmentDate',
  'entitlement',
  'totalCents',
  'components',
  ...Object.keys(MSO_FIELD_LABELS),
])

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const isDate = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
const dateText = (value: unknown): string =>
  isRedactedField(value)
    ? redactedText(value)
    : isDate(value)
      ? formatIsoDate(value)
      : value === null
        ? 'Open-ended'
        : String(value ?? 'Not supplied')
const centsText = (value: unknown): string =>
  isRedactedField(value) ? redactedText(value) : typeof value === 'number' && Number.isSafeInteger(value) ? formatCents(value) : 'Not a valid amount'
/** Any stored field as words: the withheld marker as its sentence, never "[object Object]". */
const fieldText = (value: unknown): string =>
  isRedactedField(value) ? redactedText(value) : typeof value === 'string' ? value : JSON.stringify(value)

function scalarText(name: string, value: unknown): string {
  if (isRedactedField(value)) return redactedText(value)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'string') return name.endsWith('WardClass') ? value : humaniseToken(value)
  return JSON.stringify(value)
}

export function MsoInputSummary({ input }: { input: unknown }) {
  if (!isObject(input)) {
    return (
      <p className="text-sm">
        The stored input is not in the shape the engine takes, so it is shown as it was recorded:{' '}
        <span className="pt-mono">{JSON.stringify(input)}</span>
      </p>
    )
  }
  const entitlement = isObject(input['entitlement']) ? input['entitlement'] : null
  const components = Array.isArray(input['components']) ? input['components'] : []
  const labelled = Object.keys(MSO_FIELD_LABELS).filter((name) => input[name] !== undefined)
  const others = Object.keys(input).filter((key) => !KNOWN.has(key))

  return (
    <div className="flex flex-col gap-4" data-testid="mso-input-summary">
      <dl className="grid grid-cols-[minmax(10rem,14rem)_1fr] gap-x-6 gap-y-2 text-sm max-w-2xl">
        <dt className="text-[var(--sgds-body-color-subtle)]">Date of treatment</dt>
        <dd>{dateText(input['treatmentDate'])}</dd>
        <dt className="text-[var(--sgds-body-color-subtle)]">Beneficiary reference</dt>
        <dd className="pt-mono">
          {isRedactedField(input['beneficiaryRef'])
            ? redactedText(input['beneficiaryRef'])
            : typeof input['beneficiaryRef'] === 'string'
              ? input['beneficiaryRef']
              : 'Not supplied'}
        </dd>
        {labelled.map((name) => (
          <div key={name} className="contents">
            <dt className="text-[var(--sgds-body-color-subtle)]">{MSO_FIELD_LABELS[name]}</dt>
            <dd>{scalarText(name, input[name])}</dd>
          </div>
        ))}
        <dt className="text-[var(--sgds-body-color-subtle)]">Entitlement scheme</dt>
        <dd>{entitlement === null ? 'Not supplied' : isRedactedField(entitlement['scheme']) ? redactedText(entitlement['scheme']) : String(entitlement['scheme'] ?? 'Not supplied')}</dd>
        <dt className="text-[var(--sgds-body-color-subtle)]">Entitlement valid from</dt>
        <dd>{entitlement === null ? 'Not supplied' : dateText(entitlement['validFrom'])}</dd>
        <dt className="text-[var(--sgds-body-color-subtle)]">Entitlement valid until</dt>
        <dd>{entitlement === null ? 'Not supplied' : dateText(entitlement['validUntil'])}</dd>
        <dt className="font-semibold">Total of the bill</dt>
        <dd className="font-semibold">{centsText(input['totalCents'])}</dd>
      </dl>

      {components.length > 0 && (
        <div className="pt-table-wrap">
          <table className="pt-table">
            <caption className="sr-only">The bill components as entered</caption>
            <thead>
              <tr>
                <th scope="col" data-unsortable="the components are shown in the order they were entered, which is part of the record">
                  Component
                </th>
                <th scope="col" className="pt-num" data-unsortable="the components are shown in the order they were entered, which is part of the record">
                  Amount
                </th>
                <th scope="col" className="pt-num" data-unsortable="the components are shown in the order they were entered, which is part of the record">
                  Occurrences
                </th>
              </tr>
            </thead>
            <tbody>
              {components.map((line, index) => {
                const row = isObject(line) ? line : {}
                const name = typeof row['component'] === 'string' ? row['component'] : ''
                return (
                  <tr key={index}>
                    <td>
                      {isRedactedField(row['component'])
                        ? redactedText(row['component'])
                        : (MSO_COMPONENT_LABELS[name] ?? (name === '' ? 'Not supplied' : humaniseToken(name)))}
                    </td>
                    <td className="pt-num pt-tabular">{centsText(row['amountCents'])}</td>
                    <td className="pt-num pt-tabular">{typeof row['occurrences'] === 'number' ? row['occurrences'] : 'Not supplied'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {others.length > 0 && (
        <div className="text-sm">
          <p className="font-semibold m-0 mb-1">Other recorded fields</p>
          <ul className="list-disc pl-5 m-0">
            {others.map((key) => (
              <li key={key}>
                <span className="pt-mono">{key}</span>: <span className="pt-mono">{fieldText(input[key])}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <details>
        <summary className="text-sm cursor-pointer">Show the record exactly as stored (technical record)</summary>
        {/* tabIndex so a keyboard user can scroll a long record. */}
        <pre className="pt-well p-3 text-xs overflow-x-auto mt-2" tabIndex={0} aria-label="Stored calculation input">
          {JSON.stringify(input, null, 2)}
        </pre>
      </details>
    </div>
  )
}
